import { Users } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { DataPagination } from '@/ui/DataPagination';
import { EmptyState } from '@/ui/EmptyState';
import { ErrorState } from '@/ui/ErrorState';
import { FilterBar, type FilterFieldConfig } from '@/ui/FilterBar';
import { MobileCardList, type ItemDoCard } from '@/ui/MobileCardList';
import { PortalHero } from '@/ui/PortalHero';
import { Spinner } from '@/ui/Spinner';
import { StatusBadge } from '@/ui/StatusBadge';
import { Label } from '@/ui/base/label';
import { SearchableSelect } from '@/ui/base/searchable-select';

import { CAPA_CONSULTA_DE_CREDENCIADOS } from '../imagensDeCapa';

import {
  apresentacaoDoStatusPublico,
  FILTRO_INICIAL,
  OPCAO_TODOS,
  OPCOES_DE_STATUS_PUBLICO,
  OPCOES_DE_TIPO,
  rotuloDaDre,
  rotuloDoTipo,
  temFiltroAplicado,
  type FiltroDeCredenciadosPublicos,
  type StatusPublico,
  type TipoDeCredenciadoPublico,
} from './contrato';
import { useCredenciadosPublicos, useDres } from './dados';

/**
 * Consulta pública de credenciados — `/dados-publicos/credenciados`.
 *
 * Quem está credenciado no TEG, pesquisável por qualquer pessoa da internet, sem
 * entrar no sistema.
 *
 * MINIMIZAÇÃO É O PONTO DESTA TELA. A view `public.credenciados_publico` expõe
 * seis colunas — `id`, `nome`, `nome_fantasia`, `tipo`, `status` e `dre_id` — e
 * a tela mostra cinco delas. NÃO acrescente CPF, CNPJ, telefone, e-mail nem
 * endereço: a página é aberta, e a minimização é exigência de
 * `docs/LGPD-E-ACESSIBILIDADE.md`, não preferência de tela (ver `contrato.ts`).
 *
 * NÃO HÁ TELA DE DETALHE, e por isso o cartão não é clicável: tudo o que
 * existiria dentro dela é dado que a view não expõe. O cartão é o fim da linha —
 * não é link, não é botão, e não leva a lugar nenhum. Também não há coluna de
 * ação nem de seleção (CLAUDE.md, "Regras de interface", item 7): a consulta é
 * só leitura por natureza, sem salvar, sem exportar e sem ação em lote.
 *
 * TODO O RECORTE É DO SERVIDOR — busca, filtros e paginação. A tela só guarda o
 * filtro e o entrega a `useCredenciadosPublicos`; nada é fatiado no navegador
 * depois que a página chega (CLAUDE.md, "Regras de API", item 6).
 */

/** Texto da contagem, com o plural certo para zero, um e muitos. */
function fraseDaContagem(total: number): string {
  const plural = total !== 1 ? 's' : '';
  return `${total} credenciado${plural} encontrado${plural}`;
}

export function ConsultaCredenciadosPage() {
  const [filtro, setFiltro] = useState<FiltroDeCredenciadosPublicos>(FILTRO_INICIAL);

  const consulta = useCredenciadosPublicos(filtro);
  const diretorias = useDres();

  useEffect(() => {
    document.title = 'Consulta de credenciados — Sistema TEG';
  }, []);

  /**
   * Trocar qualquer recorte volta para a primeira página. Sem isto, quem está na
   * página 4 e digita um nome cai num vazio que não é vazio — só não tem quarta
   * página do novo recorte.
   */
  function aplicar(parcial: Partial<FiltroDeCredenciadosPublicos>) {
    setFiltro((anterior) => ({ ...anterior, ...parcial, pagina: 1 }));
  }

  function limpar() {
    setFiltro(FILTRO_INICIAL);
  }

  /*
   * `FilterBar` usa string vazia como "sem recorte"; o contrato usa `TODOS`. A
   * tradução mora aqui, nos dois sentidos, para o contrato não precisar conhecer
   * a convenção do componente nem o contrário.
   */
  const campos: FilterFieldConfig[] = [
    {
      key: 'busca',
      label: 'Nome',
      type: 'search',
      placeholder: 'Buscar por nome ou nome fantasia...',
      ariaLabel: 'Buscar por nome ou nome fantasia',
    },
    {
      key: 'tipo',
      label: 'Tipo de pessoa',
      type: 'select',
      allOptionLabel: 'Todos',
      ariaLabel: 'Tipo de pessoa',
      options: OPCOES_DE_TIPO.filter((opcao) => opcao.value !== OPCAO_TODOS).map((opcao) => ({
        value: opcao.value,
        label: opcao.label,
      })),
    },
    {
      key: 'status',
      label: 'Situação',
      type: 'select',
      allOptionLabel: 'Todas',
      ariaLabel: 'Situação',
      options: OPCOES_DE_STATUS_PUBLICO.filter((opcao) => opcao.value !== OPCAO_TODOS).map(
        (opcao) => ({ value: opcao.value, label: opcao.label }),
      ),
    },
  ];

  /*
   * `dre_id` entra em `values` sem campo correspondente em `fields`: a diretoria
   * é um combobox com busca (há muitas DREs), que vai no slot `children`, mas o
   * contador de filtros ativos do `FilterBar` lê `values`. Sem esta chave, filtrar
   * por diretoria não contaria como filtro aplicado.
   */
  const valores: Record<string, string> = {
    busca: filtro.busca,
    tipo: filtro.tipo === OPCAO_TODOS ? '' : filtro.tipo,
    status: filtro.status === OPCAO_TODOS ? '' : filtro.status,
    dre_id: filtro.dre_id === OPCAO_TODOS ? '' : filtro.dre_id,
  };

  function aoTrocarCampo(chave: string, valor: string) {
    if (chave === 'busca') {
      aplicar({ busca: valor });
      return;
    }
    if (chave === 'tipo') {
      aplicar({
        tipo: valor === '' ? OPCAO_TODOS : (valor as TipoDeCredenciadoPublico),
      });
      return;
    }
    if (chave === 'status') {
      aplicar({
        status: valor === '' ? OPCAO_TODOS : (valor as StatusPublico),
      });
    }
  }

  const opcoesDeDre = useMemo(
    () => [
      { value: OPCAO_TODOS, label: 'Todas as DREs' },
      ...(diretorias.data ?? []).map((dre) => ({
        value: dre.id,
        label: dre.nome,
        code: dre.codigo,
      })),
    ],
    [diretorias.data],
  );

  const pagina = consulta.data;
  const linhas = pagina?.itens ?? [];
  const total = pagina?.total ?? 0;
  const comFiltro = temFiltroAplicado(filtro);

  /*
   * O cartão do protótipo, nos termos do design system. Sem `onClick`: não há
   * detalhe para onde ir, então o cartão fica como `<div>` — nem link, nem botão,
   * nem alvo de teclado que não leva a nada.
   */
  const cartoes: ItemDoCard[] = linhas.map((credenciado) => ({
    id: credenciado.id,
    title: credenciado.nome,
    subtitle: credenciado.nome_fantasia ?? undefined,
    badges:
      (
        /*
         * Pelo padrão de `apresentacaoDoStatusPublico`, e não pelo mapa direto:
         * um status que a API passe a devolver e este front ainda não conheça
         * derrubava a lista inteira com "cannot read properties of undefined".
         */
        <StatusBadge
          status={apresentacaoDoStatusPublico(credenciado.status).variante}
          label={apresentacaoDoStatusPublico(credenciado.status).rotulo}
        />
      ),
    fields: [
      { label: 'Tipo', value: rotuloDoTipo(credenciado.tipo) },
      {
        label: 'Diretoria regional',
        value: rotuloDaDre(credenciado.dre_id, diretorias.data),
        full: true,
      },
    ],
  }));

  return (
    <div>
      <PortalHero
        image={CAPA_CONSULTA_DE_CREDENCIADOS}
        title="Consulta de Credenciados"
        description="Pesquise credenciados no programa TEG. São exibidos apenas dados públicos — informações pessoais são protegidas conforme a LGPD."
        compact
      />

      <div className="mx-auto w-full max-w-5xl space-y-8 px-4 py-10 sm:px-6">
        <FilterBar
          fields={campos}
          values={valores}
          onChange={aoTrocarCampo}
          onClear={limpar}
          hasActiveFilters={comFiltro}
        >
          <div className="space-y-1">
            <Label>Diretoria regional</Label>
            <SearchableSelect
              value={filtro.dre_id === OPCAO_TODOS ? null : filtro.dre_id}
              onChange={(valor) => aplicar({ dre_id: valor ?? OPCAO_TODOS })}
              options={opcoesDeDre}
              placeholder="Todas as DREs"
              searchPlaceholder="Buscar diretoria..."
              emptyMessage="Nenhuma diretoria com esse nome."
              ariaLabel="Diretoria regional"
              triggerClassName="w-full sm:w-64"
              clearable={false}
              loading={diretorias.isLoading}
            />
          </div>
        </FilterBar>

        <section aria-label="Credenciados encontrados">
          {consulta.isLoading ? (
            <Spinner size="lg" rotulo="Carregando credenciados" />
          ) : consulta.isError ? (
            <ErrorState
              variant="load-failed"
              description="Não foi possível carregar a lista de credenciados agora. Verifique sua conexão e tente de novo."
              primaryAction={{ label: 'Tentar de novo', onClick: () => void consulta.refetch() }}
            />
          ) : linhas.length === 0 ? (
            <EmptyState
              variant="search"
              icon={Users}
              title="Nenhum credenciado encontrado"
              description={
                comFiltro
                  ? 'Nenhum credenciado corresponde aos filtros aplicados. Limpe os filtros ou tente outro nome.'
                  : 'Ainda não há credenciados publicados nesta consulta. Tente de novo mais tarde.'
              }
              actionLabel={comFiltro ? 'Limpar filtros' : undefined}
              onAction={comFiltro ? limpar : undefined}
            />
          ) : (
            <div className="space-y-4">
              {/*
               * Região viva: quem usa leitor de tela ouve quantos resultados o
               * novo recorte devolveu sem ter de sair do campo de busca.
               */}
              <p className="text-sm text-muted-foreground" aria-live="polite">
                {fraseDaContagem(total)}
              </p>

              <MobileCardList items={cartoes} />

              <DataPagination
                page={filtro.pagina}
                perPage={filtro.tamanho}
                total={total}
                onPageChange={(proxima) =>
                  setFiltro((anterior) => ({ ...anterior, pagina: proxima }))
                }
                onPerPageChange={(porPagina) => aplicar({ tamanho: porPagina })}
                /*
                 * O tamanho do protótipo é 20, e não está nas opções padrão do
                 * componente ([10, 25, 50, 100]) — sem trocar a lista, o seletor
                 * "Exibir" abriria em branco.
                 */
                opcoesPorPagina={[10, 20, 50, 100]}
              />
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

export default ConsultaCredenciadosPage;
